/**
 * Single source of truth for semantic GA4 events (cowork24.ch).
 *
 * The GA4 tag (`G-4WEHM6BQ6Y`) is loaded lazily via `util/DeferredAnalyticsScripts.js`.
 * Until it is ready, `window.gtag` is the lightweight stub in `public/index.html`,
 * which queues calls into `dataLayer`. Events fired before the tag finishes loading
 * are therefore replayed once gtag.js is ready.
 *
 * NOTE: Consent Mode v2 (default=denied) is deferred until a consent banner (CMP)
 * exists to grant consent — otherwise GA4 would stay in cookieless mode. See PR #11.
 *
 * No PII in clear text. For Enhanced Conversions, hash the e-mail (SHA-256) before
 * passing it to gtag — never send raw addresses through these helpers.
 */
import { googleAdsId, googleAdsBookingLabel } from '../config/configAnalytics';
import { trackOpenAiEvent } from './openai';
import { trackRedditEvent } from './reddit';

const send = (event, params = {}) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (typeof window.gtag === 'function') {
    window.gtag('event', event, params);
  }
  // Dasselbe Ereignis zusätzlich an Reddit Ads melden; die Zuordnung
  // GA4 → Reddit steht in util/reddit.js. Ohne geladenes Pixel (fehlende ID
  // oder fehlende Einwilligung) ist der Aufruf ein No-Op. Die gtag-Prüfung
  // oben ist deshalb bewusst kein vorzeitiges return mehr — sonst hätte ein
  // blockiertes gtag.js auch Reddit stummgeschaltet.
  trackRedditEvent(event, params);
  // Und an das OpenAI-Ads-Pixel (Werbung in ChatGPT); Zuordnung GA4 → OpenAI in
  // util/openai.js, ebenfalls No-Op ohne geladenes Pixel.
  trackOpenAiEvent(event, params);
};

/**
 * Builds a GA4 e-commerce `items[]` entry from a listing entity, so `view_item`,
 * `begin_checkout` and `booking_authorized` describe the item the same way (required for GA4
 * e-commerce reports, funnel exploration and item-scoped value). Two listing dimensions map to
 * GA4 categories (see CLAUDE.md city-taxonomy): `categoryLevel1` = room type, `listingType` =
 * booking model.
 */
export const buildGa4Item = listing => {
  const price = listing?.attributes?.price;
  const publicData = listing?.attributes?.publicData || {};
  return {
    item_id: listing?.id?.uuid,
    item_name: listing?.attributes?.title,
    item_category: publicData.categoryLevel1,
    item_category2: publicData.listingType,
    item_variant: publicData.city || publicData.location?.address,
    price: price ? price.amount / 100 : undefined,
    quantity: 1,
  };
};

export const track = {
  signUp: (p = {}) => send('sign_up', p), // { method, user_type }
  login: (p = {}) => send('login', p),
  listingStart: (p = {}) => send('listing_start', p),
  listingPublished: (p = {}) => send('listing_published', p), // { listing_id, type, city, price }
  beginCheckout: (p = {}) => send('begin_checkout', { currency: 'CHF', ...p }), // { value, items }
  bookingRequest: (p = {}) => send('booking_request', { currency: 'CHF', ...p }), // { listing_id, value }
  bookingConfirmed: (p = {}) => send('booking_confirmed', { currency: 'CHF', ...p }),
  // Monthly subscription (Abo, cowork24-abo process) started — month 1 paid and the
  // Stripe Billing subscription is being set up. Own semantic event so the
  // booking_request taxonomy stays clean; mark as Schlüsselereignis in GA4 and
  // import to Google Ads separately once volume exists.
  subscriptionStart: (p = {}) => send('subscription_start', { currency: 'CHF', ...p }), // { listing_id, transaction_id, value }
  // Booking payment SUCCESS = Stripe pre-authorization (money is captured at host ACCEPT), so
  // this is intentionally NOT named `purchase`. GA4-only funnel/observation signal — the direct
  // Google Ads booking conversion is fired separately via trackBookingAdsConversion().
  // Subscriptions are excluded (they fire subscription_start instead).
  bookingAuthorized: (p = {}) => send('booking_authorized', { currency: 'CHF', ...p }),
  // Outbound click on an external (affiliate) booking CTA, e.g. airport lounges → LoungePair.
  // The actual booking completes off-site, so this click is the only measurable conversion
  // signal for the affiliate flow. Mark it as a Schlüsselereignis in GA4 → import to Google Ads.
  loungeBookingClick: (p = {}) => send('lounge_booking_click', { currency: 'CHF', ...p }), // { listing_id, value, click_url }
  search: (p = {}) => send('search', p),
  // AI-Concierge auf der Suchseite: Freitext wurde geparst und als Filter
  // angewendet (oder als Stichwortsuche, wenn der Parse fehlschlug —
  // ai_fallback sagt welcher Weg). Eigenes Ereignis neben `search`, das beim
  // Laden der Ergebnisse ohnehin feuert; misst die Nutzung des Features.
  aiConciergeSearch: (p = {}) => send('ai_concierge_search', p), // { search_term, ai_fallback, ai_city, ai_category }
  viewItem: (p = {}) => send('view_item', p),
  ctaClick: (p = {}) => send('cta_click', p), // { click_text, click_url, click_id }
  generateLead: (p = {}) => send('generate_lead', p), // SUPPLY side (space-owner) — DemandGen optimizes to this
  // Absichtssignal der ANGEBOTSSEITE: Klick auf „Space inserieren" (ListSpaceCta).
  // Bewusst ein eigenes Ereignis und NICHT `generate_lead`: Letzteres feuert erst,
  // wenn der Inserate-Assistent wirklich startet, und optimiert die B2B-Kampagnen
  // damit auf echte Absicht statt auf einen Knopfdruck — diese Trennung bleibt.
  //
  // Warum es trotzdem gebraucht wird: Zwischen Landingpage und abgeschlossener
  // Registrierung sah die Messung bisher GAR NICHTS. Genau dort, an der
  // Registrierungshürde, bricht der B2B-Funnel ab. Dieses Ereignis macht den
  // Abbruch messbar und liefert die Zielgruppe für Retargeting.
  listSpaceIntent: (p = {}) => send('list_space_intent', p), // { cta_id, page_path }
  // B2C demand-side lead (inquiry/quote/offer). Deliberately a DISTINCT event from
  // `generate_lead` so it maps to its own Ads conversion action and can never pollute the
  // supply-side space-owner `generate_lead` the DemandGen campaign optimizes toward.
  contactHost: (p = {}) => send('contact_host', p), // { lead_type, listing_id, value }
  // Vertrauens-Badge und Anbieter-Stupser. Die Namen sind bewusst generisch
  // gehalten (so gewuenscht), deshalb tragen ALLE drei ein `placement` und die
  // Banner-Ereignisse zusaetzlich eine `banner_id` — ohne die waere in GA4 nicht
  // zu unterscheiden, welcher Hinweis auf welcher Flaeche gemeint ist, sobald es
  // einen zweiten Banner gibt.
  bannerShown: (p = {}) => send('banner_shown', p), // { banner_id, placement }
  bannerCtaClick: (p = {}) => send('banner_cta_click', p), // { banner_id, placement }
  badgeTooltipOpen: (p = {}) => send('badge_tooltip_open', p), // { placement }
};

// ================ Google Ads tag (remarketing) ================ //
// With REACT_APP_GOOGLE_ADS_ID set, the Google Ads tag is configured on every page next to
// GA4 (util/DeferredAnalyticsScripts.js), so Google Ads builds its own website-visitor lists
// for remarketing — independent of the GA4 → Ads audience sharing.
//
// The tag joins the gtag group `ads`, NOT the default group. Every semantic `track.*` event
// above is sent without `send_to` and therefore reaches the default group only, i.e. GA4.
// Configured into the default group, the Ads tag would receive booking_request, sign_up,
// contact_host … as well, and Google Ads would count them next to the GA4 import. Google Ads
// gets page views only (the config call + SPA navigation via trackGoogleAdsPageView), plus
// the explicit booking conversion below when its label is set.

export const GOOGLE_ADS_GROUP = 'ads';

// Google Ads gets the page without query string and fragment: /reset-password?t=…&e=… carries
// a reset token and the e-mail address, and the URL rules of the remarketing lists
// (scripts/google-ads-remarketing-lists.py) only read the path.
const adsPageLocation = () => `${window.location.origin}${window.location.pathname}`;

let adsConfigured = false;

/**
 * Configures the Google Ads tag once. SSR-safe; a no-op without a valid `AW-` id.
 *
 * @returns {boolean} true when this call configured the tag
 */
export const configureGoogleAdsTag = () => {
  if (
    adsConfigured ||
    typeof window === 'undefined' ||
    typeof window.gtag !== 'function' ||
    googleAdsId?.indexOf('AW-') !== 0
  ) {
    return false;
  }
  window.gtag('config', googleAdsId, {
    groups: GOOGLE_ADS_GROUP,
    page_location: adsPageLocation(),
  });
  adsConfigured = true;
  return true;
};

/**
 * Reports an in-app navigation to the Google Ads tag. The config call above covers the
 * landing page; without this, Google Ads would never see the listing a visitor opened next,
 * and URL rules such as "contains /l/" would only match direct landings.
 */
export const trackGoogleAdsPageView = () => {
  if (!adsConfigured || typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', 'page_view', { send_to: googleAdsId, page_location: adsPageLocation() });
};

// ================ Google Ads conversion (booking) ================ //
// The GA4 `booking_request` event above feeds GA4. This additionally fires a *direct*
// Google Ads conversion so bookings are countable in Google Ads from the first one,
// without waiting for GA4 → Ads import. Both env vars must be set (see configAnalytics.js).
// Do NOT also import the GA4 `booking_request` event as an Ads conversion — that would
// double-count against this one. Since 2026-06 the GA4 event IS imported, so the booking
// label stays unset and only the remarketing tag above is active.

// SHA-256 hex digest for Enhanced Conversions — the raw e-mail never leaves the device.
const sha256Hex = async value => {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    return undefined;
  }
  const bytes = new TextEncoder().encode(
    String(value)
      .trim()
      .toLowerCase()
  );
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Fires the Google Ads "Buchungsanfrage" conversion. Best-effort and SSR-safe; no-ops
 * unless both googleAdsId and googleAdsBookingLabel are configured.
 *
 * @param {Object} p
 * @param {number} [p.value] Booking value (CHF) — enables Ziel-ROAS later
 * @param {string} [p.currency]
 * @param {string} [p.transactionId] Used for conversion de-duplication
 * @param {string} [p.email] Buyer e-mail — SHA-256-hashed client-side (Enhanced Conversions)
 */
export const trackBookingAdsConversion = async (p = {}) => {
  if (
    typeof window === 'undefined' ||
    typeof window.gtag !== 'function' ||
    !googleAdsId ||
    !googleAdsBookingLabel
  ) {
    return;
  }
  configureGoogleAdsTag();

  if (p.email) {
    try {
      const sha256_email_address = await sha256Hex(p.email);
      if (sha256_email_address) {
        window.gtag('set', 'user_data', { sha256_email_address });
      }
    } catch (e) {
      // Enhanced Conversions is best-effort — never block the conversion on hashing.
    }
  }

  window.gtag('event', 'conversion', {
    send_to: `${googleAdsId}/${googleAdsBookingLabel}`,
    value: p.value,
    currency: p.currency || 'CHF',
    transaction_id: p.transactionId,
  });
};
